import { FC, useMemo } from 'react';
import { FormProvider } from 'react-hook-form';
import { Spinner } from '@chakra-ui/react';
import classNames from 'classnames';
import LeopartOnLightvector from '../../assets/icons/LeopartOnLightvector';
import { Users, CalendarDays, Check, TriangleAlert } from 'lucide-react';
import CustomButton from '../../shared/CustomButton/CustomButton';
import Typhography from '../../shared/Typhography/Typhography';
import { CustomFormV3 } from '../../shared/CustomFormV3/CustomForm';
import { useJoinTeam } from './useJoinTeam';
import styles from './JoinTeamPage.module.scss';

/** Iniziali della squadra, come il badge del dettaglio team. */
const teamInitials = (name?: string) =>
  (name ?? '')
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((word) => word[0])
    .join('')
    .toUpperCase() || '—';

/**
 * Pagina che l'atleta apre col link ricevuto (`/join/<code>`).
 *
 * È pubblica: nessun token, nessuna entità selezionata. Prima si sceglie fra
 * accedere e registrarsi, poi si compila solo ciò che serve a quella strada.
 * Campi e validazione vivono nell'hook; qui resta la sola presentazione.
 */
const JoinTeamPage: FC = () => {
  const {
    methods,
    layout,
    pageState,
    isRegistering,
    preview,
    generalError,
    isSubmitting,
    result,
    isFormComplete,
    invalidMessage,
    chooseMode,
    backToChoice,
    submit,
    currentUser,
    joinAsCurrentUser,
    useAnotherAccount,
  } = useJoinTeam();

  const expiryLabel = preview?.expiresAt
    ? new Date(preview.expiresAt).toLocaleDateString('it-IT')
    : null;

  const initials = useMemo(
    () => teamInitials(preview?.teamName),
    [preview?.teamName],
  );

  /** Intestazione comune alla schermata di scelta e ai moduli. */
  const header = (
    <>
      <div className={styles.teamBadge}>{initials}</div>

      <Typhography size="regular-14" className={styles.title}>
        Unisciti alla squadra
      </Typhography>
      <Typhography type="h1" size="bold-20" className={styles.teamName}>
        {preview?.teamName}
      </Typhography>

      {(preview?.remainingUses != null || expiryLabel) && (
        <div className={styles.details}>
          {preview?.remainingUses != null && (
            <span className={styles.detail}>
              <Users size={13} strokeWidth={2.2} />
              {preview.remainingUses} posti rimasti
            </span>
          )}
          {expiryLabel && (
            <span className={styles.detail}>
              <CalendarDays size={13} strokeWidth={2.2} />
              Valido fino al {expiryLabel}
            </span>
          )}
        </div>
      )}

      <div className={styles.divider} />
    </>
  );

  return (
    <div className={styles.page}>
      {/* Header dedicato: `HeaderLogin` porta con sé il CTA "Entra nella Beta
          Coach", che qui sarebbe fuori contesto — l'atleta sta entrando in una
          squadra, non valutando il prodotto. */}
      <header className={styles.brand}>
        <LeopartOnLightvector w="34px" h="34px" />
        <span className={styles.brandName}>Leopart</span>
      </header>

      <div className={styles.content}>
        <div className={styles.card}>
          {pageState === 'loading' && (
            <div className={styles.loading}>
              <Spinner />
            </div>
          )}

          {pageState === 'invalid' && (
            <div className={styles.centered}>
              <div
                className={classNames(styles.statusIcon, styles.statusIconWarn)}
              >
                <TriangleAlert size={26} strokeWidth={2.2} />
              </div>
              <Typhography type="h1" size="bold-20" className={styles.teamName}>
                Link non utilizzabile
              </Typhography>
              <Typhography size="regular-14" className={styles.message}>
                {invalidMessage}
              </Typhography>
              <a className={styles.link} href="/login">
                Vai al login
              </a>
            </div>
          )}

          {pageState === 'unreachable' && (
            <div className={styles.centered}>
              <div
                className={classNames(styles.statusIcon, styles.statusIconWarn)}
              >
                <TriangleAlert size={26} strokeWidth={2.2} />
              </div>
              <Typhography type="h1" size="bold-20" className={styles.teamName}>
                Non riusciamo a caricare l'invito
              </Typhography>
              <Typhography size="regular-14" className={styles.message}>
                Il link potrebbe essere valido: al momento non riusciamo a
                contattare il server. Riprova fra qualche minuto.
              </Typhography>
              <button
                type="button"
                className={styles.link}
                onClick={() => window.location.reload()}
              >
                Riprova
              </button>
            </div>
          )}

          {pageState === 'done' && result && (
            <div className={styles.centered}>
              <div
                className={classNames(styles.statusIcon, styles.statusIconOk)}
              >
                <Check size={26} strokeWidth={2.6} />
              </div>
              <Typhography type="h1" size="bold-20" className={styles.teamName}>
                {result.alreadyMember ? 'Sei già in squadra' : 'Ci sei!'}
              </Typhography>
              <Typhography size="regular-14" className={styles.message}>
                {result.alreadyMember
                  ? `Fai già parte di ${result.teamName}.`
                  : `Sei stato aggiunto a ${result.teamName}.`}{' '}
                Accedi con la tua email per iniziare.
              </Typhography>
              <a className={styles.link} href="/athlete/login">
                Vai al login
              </a>
            </div>
          )}

          {pageState === 'signedIn' && currentUser && (
            <>
              {header}

              <Typhography size="regular-14" className={styles.hint}>
                Sei connesso come
              </Typhography>

              <div className={styles.account}>
                <div className={styles.accountAvatar}>
                  {(currentUser.firstName?.[0] ?? currentUser.email[0] ?? '?').toUpperCase()}
                </div>
                <div className={styles.accountInfo}>
                  {(currentUser.firstName || currentUser.lastName) && (
                    <Typhography size="bold-14" className={styles.accountName}>
                      {[currentUser.firstName, currentUser.lastName]
                        .filter(Boolean)
                        .join(' ')}
                    </Typhography>
                  )}
                  <Typhography size="regular-12" className={styles.accountEmail}>
                    {currentUser.email}
                  </Typhography>
                </div>
              </div>

              {generalError && <div className={styles.error}>{generalError}</div>}

              <div className={styles.actions}>
                <CustomButton
                  fullWidth
                  color="secondary"
                  className="join-as-me"
                  disabled={isSubmitting}
                  isLoading={isSubmitting}
                  onClick={joinAsCurrentUser}
                >
                  Entra in squadra
                </CustomButton>
                <CustomButton
                  fullWidth
                  color="text"
                  size="sm"
                  className="join-other-account"
                  onClick={useAnotherAccount}
                >
                  Usa un altro account
                </CustomButton>
              </div>
            </>
          )}

          {pageState === 'choice' && (
            <>
              {header}
              <Typhography size="regular-14" className={styles.hint}>
                Come vuoi procedere?
              </Typhography>

              <div className={styles.choices}>
                <CustomButton
                  fullWidth
                  color="secondary"
                  className="join-choice-register"
                  onClick={() => chooseMode('register')}
                >
                  Registrati
                </CustomButton>
                <CustomButton
                  fullWidth
                  color="outline"
                  className="join-choice-login"
                  onClick={() => chooseMode('login')}
                >
                  Ho già un account
                </CustomButton>
              </div>
            </>
          )}

          {pageState === 'form' && (
            <>
              {header}

              <Typhography size="regular-14" className={styles.hint}>
                {isRegistering
                  ? 'Crea il tuo account per entrare in squadra.'
                  : 'Accedi con le credenziali del tuo account LeoPart.'}
              </Typhography>

              <FormProvider {...methods}>
                <form
                  className={styles.form}
                  onSubmit={(event) => {
                    event.preventDefault();
                    submit();
                  }}
                >
                  <CustomFormV3 layout={layout} gridProps={{ gap: 4 }} />

                  {generalError && (
                    <div className={styles.error}>{generalError}</div>
                  )}

                  <div className={styles.actions}>
                    <CustomButton
                      fullWidth
                      color="secondary"
                      btnType="submit"
                      type="submit"
                      className="join-submit"
                      disabled={!isFormComplete || isSubmitting}
                      isLoading={isSubmitting}
                    >
                      {isRegistering ? 'Registrati ed entra' : 'Accedi ed entra'}
                    </CustomButton>

                    <CustomButton
                      fullWidth
                      color="text"
                      size="sm"
                      className="join-back"
                      onClick={backToChoice}
                    >
                      Indietro
                    </CustomButton>
                  </div>
                </form>
              </FormProvider>
            </>
          )}
        </div>
      </div>

      <footer className={styles.footer}>
        <a href="/privacy">Privacy Policy</a>
        <span className={styles.footerDot}>·</span>
        <a href="/terms">Termini e condizioni</a>
      </footer>
    </div>
  );
};

export default JoinTeamPage;
